import React, { memo, useRef, useEffect, useCallback, useState} from 'react';
import {SCRIPT, SWAP, SPIRAL, ROTATE, BOUNCE_1, BOUNCE_2, LIFE, NONE} from 'effects/midi/mappable/lang/operations.js';
import CanvasManager from './CanvasManager.js';
import Plane3Canvas from './Plane3Canvas.js';
import Forces from './Forces.js';
import CanvasRegions from "components/effects/plane/CanvasRegions.js";
import ShaderCanvas from "components/effects/plane/ShaderCanvas.js";
import PlaneCanvas from './PlaneCanvas.js';
import { ReactReduxContext, Provider } from 'react-redux';
import { FastLayer, Rect, Group, Stage, Layer, Circle, Line} from 'react-konva';
import {getCoordinates} from "effects/midi/mappable/getCoordinates.js";
import PlaneMacro from './PlaneMacro.js';
import DrawnPath from './DrawnPath.js';
import {useDropPreset} from './useDropPreset.js';
import {useActions} from "components/effects/plane/useActions.js";
import PlaneToolbar from "components/effects/plane/PlaneToolbar.js";
import {PLANAR_SCRIPT} from 'constants/script-types.js';
import UserScriptComponent from "components/effects/scripting/UserScriptComponent.js";
import {doc} from 'effects/midi/mappable/lang/documentation.js';
import {useDensities} from './useDensities.js';
import {dist} from './utils.js';
import ConditionedComponent from "components/ConditionedComponent.js";
import PlaneSnapshots from './PlaneSnapshots.js';
import {useKey} from './useKey.js';
import RegionMode from './RegionMode.js';
import PlaneAlgo from './PlaneAlgo.js';
import PlaneGroup from './PlaneGroup.js';
import {useWheel} from './useWheel.js';
import PlaneSidebar from './PlaneSidebar.js';
import {
    setPlanarRegionMode,
    setPlanarMacroToShow,
    setFilteredPlanarParameters,
    setSelectedPlanarParameters,
    setEffectParameterValues} from 'actions/index.js';
import {setParameterMinMax} from 'actions/index.js';
import Colors from 'components/Colors.js';
import {useMouse} from './useMouse.js';
import {connect} from 'react-redux';
import {WIDTH, HEIGHT, PADDING} from './constants.js';
import '../../../Plane.scss';

const colors = new Colors();


const Plane = ({
    setParameterMinMax,
    effect,
    parameterSelected,
    setEffectParameterValues,
    sequencerEngine,
    macros=[],
    filteredParameters,
    setFilteredParameters,
    selected=[],
    setSelected,
    macroToShow,
    setMacroToShow,
    isRegionMode,
    setIsRegionMode,
    hideUI,
    ... props
}) => { 
      

    let [selectedColor, setSelectedColor] = useState(undefined);
    let ref = useRef(undefined);

    let [expanded, setExpanded] = useState(true);

    let {
        isDensitiesMode,
        setIsDensitiesMode,
        onClickDensity,
        setSelectedDensity,
        selectedDensity,
        densities,
        setDensities} = useDensities(effect);

    let {
        setDrawMode,
        drawMode,
        selection,
        startDragging,
        onMouseDown,
        onMouseMove,
        onMouseUp,
        } = useMouse({
            ref,
            setSelectedDensity,
            selected,
            setSelected,
            setDensities,
            effect,
            setEffectParameterValues,
            filteredParameters,
            isRegionMode,
            isDensitiesMode,
            macroToShow
    });

    useEffect(() => {
        if (selected && !filteredParameters) {
            if (selected.length == 0) {
                setMacroToShow(undefined);
            } else if (macroToShow && selected[0].isPlaneMacro && selected[0] !== macroToShow) {
                setMacroToShow(selected[0]);
            }
        }
    }, [selected, setMacroToShow, filteredParameters, macroToShow]);

    useEffect(() => {
        if (!filteredParameters) {
            return;
        }
        let p = filteredParameters.find(x => x.planeMacro);
        if (p) {
            setMacroToShow(p.planeMacro);
        }
    }, [filteredParameters, setMacroToShow]);

    const {mergeGroups, lock, locked, grouped} = useActions({
        effect, selected, setSelected, parameterSelected, setIsRegionMode});

    const {onDropPreset} = useDropPreset(effect);
    const {unmap, group} = useKey({
        selectedDensity,
        setDensities,
        effect,
        setSelected,
        selected,
        filteredParameters,
        setFilteredParameters,
        sequencerEngine,
        expanded,
        setEffectParameterValues,
        setParameterMinMax,
        isRegionMode});

    useWheel({
        ref,
        effect,
        setDensities,
        selectedDensity,
        setEffectParameterValues,
        selected,
        setSelected,
        isRegionMode});

    const height = selection && Math.abs(selection.y2 - selection.y1);
    const width = selection && Math.abs(selection.x2 - selection.x1);
    let parameters  = filteredParameters || effect._parameters.filter(
        s => (macroToShow && macroToShow === s.planeMacro) || !s.planeMacro);

    let coords = {};
    getCoordinates(parameters)
        .forEach((c, i) => coords[parameters[i].id] =c);

    // if there are 2 or more groups (non-grouped counts as a group here),
    // then we allow the user to merge those together

   
    let className = "plane-inner";
    if (expanded) {
        className += " expanded";
    } 

    return (
        <div
          onDrop={onDropPreset}
          className={className}>
        <div className="vertical-pane">
          <div className="plane-container">
            <div
              draggable={true}
              ref={ref}
              onDragStart={e => e.preventDefault()}
              onDragOver={(e) => e.preventDefault()}
              onMouseDown={onMouseDown}
              onMouseUp={onMouseUp}
              onMouseMove={onMouseMove}
              className="plane-view">
              <div className="y-axis">
                Modulation
              </div>
              <div className="x-axis">
                Parameter Value
              </div>
              <svg
                className="drawn-svg" height={HEIGHT} width={WIDTH}>
                {!window.isNFT && <DrawnPath drawnPath={effect.currentPath} effect={effect}/>}
                </svg>
             <CanvasManager
                blur={props.blur}
                selectedColor={selectedColor}
                setMacroToShow={setMacroToShow}
                startDragging={startDragging}
                coords={coords}
                filteredParameters={filteredParameters}
                parameters={parameters}
                macroToShow={macroToShow}
                setParameterMinMax={setParameterMinMax}
                effect={effect}
                parameterSelected={parameterSelected}
                setEffectParameterValues={setEffectParameterValues}
                containerRef={ref}
                selected={selected}
                selection={selection}
                sequencerEngine={sequencerEngine}
                isRegionMode={isRegionMode}
                macros={macros}
                {... props}
              />
            </div>
         </div>
        {<PlaneToolbar
            setSelected={setSelected}
            isDraggable={props.isDraggable}
            drawMode={drawMode}
            setDrawMode={setDrawMode}
            isRegionMode={isRegionMode}
            effect={effect}
            setExpanded={setExpanded}
            expanded={expanded}
            setIsRegionMode={setIsRegionMode}
            unmap={unmap}
            selected={selected}
            mergeGroups={mergeGroups}
            group={group}
            grouped={grouped}
          >
            {props.children}
         </PlaneToolbar>}
       </div>
          {!hideUI && expanded && effect.groups[0] &&
           effect.groups[0].algorithm === SCRIPT ?
           <UserScriptComponent
             isDraggable={props.isDraggable}
             scriptType={PLANAR_SCRIPT}
             expanded={true}
             setExpanded={() => {
                 effect.scriptExpanded = undefined;
                 setExpanded(undefined);
             }}
             doc={doc}
             sequencerEngine={sequencerEngine}
             effect={effect.groups[0]}/> : ''}
        </div>
    );
};

const Star = memo(({bpm, parameter, isSelected, startDragging, x, y}) => {
    let fill = colors.getColorForHash(parameter.name);
    let stroke = fill;
    if (isSelected) {
        fill = "black";
    }
    let blur = (bpm / 85.0);
    let style = {
    };
    if (window.isNFT) {
        style.filter= "blur(" + blur + 'px)';
    }
    if (isSelected) {
        return (
            <Circle
              perfectDrawEnabled={false}
              shadowForStrokeEnabled={false}
              hitStrokeWidth={0}
              style={style}
              onMouseDown={(e) => startDragging(e.evt, parameter)}
              opacity={parameter.locked2D ? 0.3 : 1}
              className={isSelected ? "star selected" : "selected"}
              x={Math.round(x)} y={Math.round(y)} radius={7} fill={fill} stroke={stroke}/>
        );
    } else {
        return (
            <Circle
              shadowForStrokeEnabled={false}
              hitStrokeWidth={0}
              perfectDrawEnabled={false}
              style={style}
              opacity={parameter.locked2D ? 0.3 : 1}
              onMouseDown={(e) => startDragging(e.evt, parameter)}
              className={isSelected ? "star selected" : "selected"}
              x={Math.round(x)} y={Math.round(y)} radius={7} fill={fill}/>
        );
    }
});

const mapStateToProps = (state, props) => {
    let x ={
    };
    props.effect._parameters
        .map(
        p => x[p.id] = state.effects.effectParameters[p.id]);
    props.effect._parameters
        .map(
        p => x[p.id + 'depth'] = p.depth);
    return {
        ...x,
        macros: state.plane.macros[props.effect.id],
        parameterSelected: state.effects.accumulatorParameterSelected,
        bpm: props.effect.track.sequencerEngine.bpm,
        blur: props.effect.blurParameter.get(),
        selected: state.plane.selectedParameters[props.effect.id],
        filteredParameters: state.plane.filteredParameters[props.effect.id],
        macroToShow: state.plane.macroToShow[props.effect.id],
        isRegionMode: state.plane.regionMode[props.effect.id]
    };
};

const mapDispatchToProps = (dispatch, props) => {
    return {
        setIsRegionMode: x => dispatch(setPlanarRegionMode(props.effect.id, x)),
        setMacroToShow: x => dispatch(setPlanarMacroToShow(props.effect.id, x)),
        setSelected: x => dispatch(setSelectedPlanarParameters(props.effect.id, x)),
        setFilteredParameters: x => dispatch(setFilteredPlanarParameters(props.effect.id, x)),
        setEffectParameterValues: x => dispatch(setEffectParameterValues(x)),
        setParameterMinMax: x => dispatch(setParameterMinMax(x))
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(Plane);
